export type ConfirmationLanguageCode = 'vi' | 'en' | string;

export interface ConfirmationLabels {
  fullName: string;
  memberCode: string;
  phone: string;
  service: string;
  activationDate: string;
  expiryDate: string;
  visitBalance: string;
  paymentStatus: string;
  paymentMethod: string;
}

export interface ConfirmationSupport {
  label: string;
  channel: string;
  phone: string;
  workingHours: string;
  additionalText: string;
}

export interface ConfirmationTranslation {
  title: string;
  customerSectionTitle: string;
  productSectionTitle: string;
  termsSectionTitle: string;
  labels: ConfirmationLabels;
  terms: string[];
  support: ConfirmationSupport;
}

export interface ConfirmationTemplate {
  defaultLanguage: ConfirmationLanguageCode;
  availableLanguages: ConfirmationLanguageCode[];
  translations: Record<ConfirmationLanguageCode, ConfirmationTranslation>;
}

export interface ConfirmationCustomer {
  fullName: string;
  memberCode: string;
  phone: string | null;
  email: string | null;
}

export interface ConfirmationProduct {
  serviceName: string;
  serviceTypeName: string;
  packageName: string;
  displayName: string;
  serviceFor: string;
  activationDate: string;
  expiryDate: string;
  visitBalance: number | null;
  visitType: string;
  amount: number;
  subAmount: number | null;
  duration: number;
  courseLevel: string | null;
  price: number;
  priceForChild: number | null;
  discountPrice: number;
}

export interface ConfirmationPayment {
  method: string;
  methodLabel: string;
  statusLabel: string | null;
}

export interface RegistrationConfirmation {
  registrationId: number;
  template: ConfirmationTemplate;
  customer: ConfirmationCustomer;
  product: ConfirmationProduct;
  payment: ConfirmationPayment;
}

const createEmptyLabels = (): ConfirmationLabels => ({
  fullName: '',
  memberCode: '',
  phone: '',
  service: '',
  activationDate: '',
  expiryDate: '',
  visitBalance: '',
  paymentStatus: '',
  paymentMethod: '',
});

const createEmptySupport = (): ConfirmationSupport => ({
  label: '',
  channel: '',
  phone: '',
  workingHours: '',
  additionalText: '',
});

const createEmptyTranslation = (): ConfirmationTranslation => ({
  title: '',
  customerSectionTitle: '',
  productSectionTitle: '',
  termsSectionTitle: '',
  labels: createEmptyLabels(),
  terms: [],
  support: createEmptySupport(),
});

const createEnglishTranslation = (): ConfirmationTranslation => ({
  ...createEmptyTranslation(),
  title: 'AQUATICS HUB — SERVICE REGISTRATION CONFIRMATION',
  customerSectionTitle: 'Customer information:',
  productSectionTitle: 'Product details:',
  termsSectionTitle: 'Terms and conditions:',
  labels: {
    fullName: 'Full name',
    memberCode: 'Member code',
    phone: 'Phone number',
    service: 'Service package',
    activationDate: 'Activation date',
    expiryDate: 'Expiry date',
    visitBalance: 'Visit balance',
    paymentStatus: 'Payment status',
    paymentMethod: 'Payment method',
  },
  terms: [
    'Each scan is valid for one customer and one swimming visit.',
    'Hold/extension policy: Refunds are not available. A package hold is only applicable with medical confirmation.',
    'Customers must present their membership code or provide their phone number at Reception when entering the pool.',
  ],
  support: {
    label: 'Support:',
    channel: 'Reception Hotline/Zalo',
    phone: '0969685445',
    workingHours: '06:00 - 20:30',
    additionalText: '',
  },
});

const createVietnameseTranslation = (): ConfirmationTranslation => ({
  title: 'AQUATICS HUB — XÁC NHẬN ĐĂNG KÝ DỊCH VỤ',
  customerSectionTitle: 'Thông tin khách hàng:',
  productSectionTitle: 'Chi tiết sản phẩm:',
  termsSectionTitle: 'Quy định & Điều khoản:',
  labels: {
    fullName: 'Họ và tên',
    memberCode: 'Mã hội viên',
    phone: 'Số điện thoại',
    service: 'Gói dịch vụ',
    activationDate: 'Ngày kích hoạt',
    expiryDate: 'Ngày hết hạn',
    visitBalance: 'Số lượt sử dụng',
    paymentStatus: 'Trạng thái thanh toán',
    paymentMethod: 'Phương thức thanh toán',
  },
  terms: [
    'Mỗi lượt quét tương ứng với 01 khách hàng/lượt bơi.',
    'Chính sách bảo lưu/gia hạn: Không áp dụng hoàn tiền. Việc bảo lưu gói chỉ áp dụng khi có xác nhận y tế.',
    'Khách hàng vui lòng xuất trình mã hội viên hoặc đọc SĐT tại Lễ tân khi vào bể.',
  ],
  support: {
    label: 'Kênh hỗ trợ:',
    channel: 'Hotline/Zalo Lễ tân',
    phone: '0969685445',
    workingHours: '06:00 - 20:30',
    additionalText: '',
  },
});

export const createDefaultConfirmationTemplate = (): ConfirmationTemplate => ({
  defaultLanguage: 'vi',
  availableLanguages: ['vi', 'en'],
  translations: {
    vi: createVietnameseTranslation(),
    en: createEnglishTranslation(),
  },
});

const cloneTranslation = (
  translation: ConfirmationTranslation,
): ConfirmationTranslation => ({
  ...translation,
  labels: { ...translation.labels },
  terms: [...translation.terms],
  support: { ...translation.support },
});

export const normalizeConfirmationTemplate = (
  template?: ConfirmationTemplate | null,
): ConfirmationTemplate => {
  const fallback = createDefaultConfirmationTemplate();
  if (!template) return fallback;

  const availableLanguages = Array.from(
    new Set([
      'vi',
      'en',
      template.defaultLanguage,
      ...(template.availableLanguages || []),
      ...Object.keys(template.translations || {}),
    ]),
  );

  const translations = availableLanguages.reduce<
    Record<ConfirmationLanguageCode, ConfirmationTranslation>
  >((result, language) => {
    const base =
      language === 'vi'
        ? fallback.translations.vi
        : fallback.translations[language] || createEmptyTranslation();
    const configured = template.translations?.[language];
    const configuredSupport = configured?.support;
    const hasEnglishSupportContent = Object.values(
      configuredSupport || {},
    ).some((value) => value.trim().length > 0);
    const support =
      language === 'en' && configuredSupport && !hasEnglishSupportContent
        ? base.support
        : { ...base.support, ...configuredSupport };

    result[language] = cloneTranslation({
      ...base,
      ...configured,
      labels: { ...base.labels, ...configured?.labels },
      terms: configured?.terms ? [...configured.terms] : [...base.terms],
      support,
    });
    return result;
  }, {});

  return {
    defaultLanguage: template.defaultLanguage || fallback.defaultLanguage,
    availableLanguages,
    translations,
  };
};
